<template>
  <div class="w-full grid grid-cols-2 gap-x-3 gap-y-1 p-1.5">
    <BaseText type="label" size="sm" class="text-text-muted">
      From
    </BaseText>
    <BaseText type="label" size="sm" class="text-text-muted">
      To
    </BaseText>
    <input 
      ref="min-input"
      v-model="timeValues.min"
      @input="validateTimeInput($event, 'min')"
      @blur="handleBlur"
      @keydown="handleKeyboardNavigation($event, 'min')"
      class="form-input px-2 py-1.5"
      placeholder="0:00"
    />
    <input 
      ref="max-input"
      v-model="timeValues.max"
      @input="validateTimeInput($event, 'max')"
      @blur="handleBlur"
      @keydown="handleKeyboardNavigation($event, 'max')"
      class="form-input px-2 py-1.5"
      placeholder="0:00"
    />
  </div>
</template>

<script>
import TagRemoveIcon from '../../../globals/Icons/TagRemoveIcon.vue'
import { mixin as clickaway } from 'vue-clickaway2'

export default {
  name: 'VideoLengthFilter',
  components: {
    TagRemoveIcon
  },
  mixins: [clickaway],
  props: {
    selection: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      timeValues: {
        min: '',
        max: ''
      }
    }
  },
  created () {
    this.timeValues.min = this.selection.min ? this.formatTimeFromSeconds(this.selection.min) : ''
    this.timeValues.max = this.selection.max ? this.formatTimeFromSeconds(this.selection.max) : ''
  },
  methods: {
    handleBlur () {
      this.formatTimeInput('min')
      this.formatTimeInput('max')
      this.submitTimeValues()
    },
    submitTimeValues () {
      const { min, max } = this.timeValues
      const updatedValues = {
        min: min?.length ? this.parseSecondsFromTime(min) : null,
        max: max?.length ? this.parseSecondsFromTime(max) : null
      }
      if (updatedValues.min !== this.selection.min || updatedValues.max !== this.selection.max) {
        if (updatedValues.min === null && updatedValues.max === null) {
          this.$emit('update', null)
        } else {
          this.$emit('update', updatedValues)
        }
      }
    },

    // Keyboard Navigation
    handleKeyboardNavigation (event, type) {
      const validKeys = ['ArrowLeft', 'ArrowRight', 'Enter', 'Escape', 'Tab']
      if (!validKeys.includes(event.key)) return
      if (['ArrowLeft', 'ArrowRight'].includes(event.key) && this.timeValues[type]?.length) return
      event.preventDefault()

      if (event.key === 'ArrowLeft') {
        if (type === 'min') {
          this.$emit('focusPrev')
          this.$emit('update:isExpanded', false)
        }
        else this.$refs['min-input'].focus()
      } else if (['Tab', 'ArrowRight'].includes(event.key)) {
        if (type === 'max') {
          this.$emit('focusNext')
          this.$emit('update:isExpanded', false)
        }
        else this.$refs['max-input'].focus()
      } else if (['Enter', 'Escape'].includes(event.key)) {
        this.submitTimeValues()
      }
    },

    formatTimeFromSeconds (seconds) {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      return `${minutes.toString().padStart(1, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
    },
    validateTimeInput (event, type) {
      // Ensure only numbers and at most one colon are allowed
      // Only allow a colon input if a number has already been entered

      let value = event.target.value
      const regex = /\d/.test(value) ? /[^0-9:]/g : /[^0-9]/g
      value = value.replace(regex, '')
      if (value.includes(':')) {
        const parts = value.split(':')
        if (parts.length > 2) {
          value = parts.slice(0, 2).join(':')
        }
      }      
      this.timeValues[type] = value
    },
    parseSecondsFromTime (time) {
      const parts = time.split(':').reverse()
      return parts.reduce((acc, part, index) => {
        return acc + parseInt(part) * Math.pow(60, index)
      }, 0)
    },
    formatTimeInput (type) {
      const value = this.timeValues[type]
      if (!value.length) {
        this.timeValues[type] = ''
        return
      }
      let seconds = this.parseSecondsFromTime(value)
      
      // Enforce that min <= max and max >= min
      if (type === 'min' && this.timeValues?.max?.length) {
        const maxSeconds = this.parseSecondsFromTime(this.timeValues.max)
        if (seconds > maxSeconds) {
          seconds = maxSeconds
          this.highlightConstraint('max')
        }
      } else if (type === 'max' && this.timeValues?.min?.length) {
        const minSeconds = this.parseSecondsFromTime(this.timeValues.min)
        if (seconds < minSeconds) {
          seconds = minSeconds
          this.highlightConstraint('min')
        }
      }

      this.timeValues[type] = this.formatTimeFromSeconds(seconds)
    },
    highlightConstraint (type) {
      const input = this.$refs[`${type}-input`]
      if (input) {
        input.classList.add('highlight-constraint')
        setTimeout(() => {
          input.classList.remove('highlight-constraint')
        }, 750)
      }
    }
  }
}
</script>

<style scoped>
.container {
  width: auto;
  transition: padding-top 75ms ease-in-out, padding-bottom 75ms ease-in-out;
  padding-top: 0;
  padding-bottom: 0;
}
.container.expanded {
  width: 200px;
  padding-top: 10px;
  padding-bottom: 10px;
}

.dropdown {
  border-radius: 10px;
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.13), 0 0 0 1px rgba(0, 56, 108, 0.08), 0 0 0 1px #FFF;
  height: 20px;
  z-index: 1000;
  overflow: hidden;
  animation: dropdownEnter 75ms ease-out forwards;
}
@keyframes dropdownEnter {
  from { height: 20px; }
  to { height: 80px; }
}

.form-input {
  color: #06070F;
  border-radius: 6px;
  border: 1px solid;
  border-color: transparent;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: border-color 150ms ease-in-out, box-shadow 150ms ease-in-out;

  /* Body/Small */
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
}
.form-input:focus {
  outline: none;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.1), 0px 0px 0px 1px rgba(0, 56, 108, 0.1);
}
.form-input::placeholder {
  color: #C1C7D0;
  transition: color 150ms ease-in-out;
  opacity: 0.9;
}
.form-input.invalid {
  box-shadow: none;
  border-color: #FF002F;
}
.form-input:hover::placeholder {
  color: #C1C7D0;
}
.form-input:focus::placeholder {
  color: #C1C7D0;
}

.highlight-constraint {
  animation: highlightConstraint 750ms ease-in-out;
}
@keyframes highlightConstraint {
  0% {
    box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  }
  50% {
    box-shadow: 0px 1px 2px 0px #ED8296, 0px 0px 0px 1px #ED8296;
  }
  100% {
    box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  }
}
</style>